import { Component } from 'react'
import _ from 'lodash'
import { authenticated, loaded, getInitSettings, getFrameworkVar, hasMicroApp } from '../../framework/states'
import './Main.scss'

import {
  doLogin,
  unsetSession,
  getUserLoggedInInfo,
  getMd5Salt,
  clearLoadingMask,
} from './ui-lib/utils'
import {
  GPCS_CONTAINERS,
  PRISMA_ACCESS_KEY,
} from './ui-lib/constants'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalCloseButton,
} from '@panwds/react-ui'

import RouteViewer from './RouteViewer'
import { withRouter } from 'react-router-dom'
import AppBar from './AppBar'
import { RouteProvider } from './RouteContext'
import '@pan/cortex-app-bar'
import logo from '../../images/Prisma-Access-Logo.svg'
import collapsedLogo from '../../images/Prisma-Access-Collapsed-Logo.svg'
import StartupManager from './StartupManager'
import LeftNavWrap, { debounceSave } from './LeftNav'
import { ComponentWrapper, MemoComponentWrapper } from './RouteWrapper'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { _T } from '../../i18n'
import { __info } from '../../init/debug'
import {
  SPARKY_FRAMEWORK,
  MGMT_OVERVIEW_ROUTE,
} from '../../framework/constants'
import { getPendo } from '../../framework/externals'
import { hasTsgSupport } from '../../framework/tsg'
import { openTenantSwitcher, setMainValues, setValuesInternal, logout } from '../../framework/actions'
import { isInSilentLoginIframe, tryParseJson } from '../../init'
import { loadFawkesMainInfo, syncLocationOnce, handleOidcLogin } from './handleLogin'
import mspActions, { Announcements } from './MspActions'
import { isNonemptyArray } from '../../framework/utils'
import { initCache } from '../../framework/cache'

class Main extends Component {
  constructor(props) {
    super(props)

    const { history, state } = props
    history.listen(this.routeChange)

    this.lite = Boolean(getInitSettings().lite)
    this.vars = state.vars?.[SPARKY_FRAMEWORK] || {}
    this.pendo = getPendo()
  }

  inHubMode = () => {
    return Boolean(this.props.state.commonModule?.hubMode)
  }

  componentDidUpdate() {
    if (this.vars.msp_tenant_switcher || this.vars.sase || this.vars.hub || this.vars.private_site_origin) {
      return // skip there is no user dropdown from appbar, and no app switcher
    }
    const { classicAppSwitcher } = this.props.state
    const cortexAppBar = document.querySelector('cortex-app-bar')
    if (cortexAppBar && !this.cortexAppBar) {
      cortexAppBar.addEventListener('cortex-sign-out', () => {
        clearTimeout(cortexAppBar.__log_out_to)
        cortexAppBar.__log_out_to = setTimeout(logout, 250)
      })
      cortexAppBar.onCollapseBtnClick = this.collapseBtnClicked
      if (classicAppSwitcher.visible) {
        setTimeout(() => {
          const { classicAppSwitcher } = this.props.state
          const loading = classicAppSwitcher.loading ?? !classicAppSwitcher.entitlements
          if (loading) {
            cortexAppBar.setAppSwitcherLoading(true)
            cortexAppBar._set_loading = true
          }
        }, 100)
      }
      this.cortexAppBar = cortexAppBar
    }
    if (classicAppSwitcher.visible && cortexAppBar?._set_loading && classicAppSwitcher.entitlements) {
      cortexAppBar.setAppSwitcherLoading(false)
    }
  }

  relogin = () => {
    if (this.props.oidc) {
      const { userManager, login } = this.props.oidc
      return userManager.removeUser().then(login) // will clear session storage
    }
    else {
      return doLogin()
    }
  }

  changeLocation = (pathname, customKey) => {
    const key = customKey || pathname.split('/')[2]
    const container = GPCS_CONTAINERS[key]
    if (container) {
      // this.changeConfigLocation({ container })
      setValuesInternal({
        locationRoute: key,
        main: {
          ...this.props.main,
          configLocation: { container }
        }
      })
    }
  }

  changeConfigLocation = (container) => {
    setMainValues({ configLocation: container })
  }

  routeChange = (location, action) => {
    const pathname = location.pathname

    if (pathname.startsWith(MGMT_OVERVIEW_ROUTE)) {
      this.changeLocation('', PRISMA_ACCESS_KEY)
    }

    if (!_.isEmpty(this.props.state.loggedInUser)) {
      const lastVisitedUrl = pathname
      const { userPrefStore } = _.get(this.props.state.loggedInUser, 'preference', {})
      const userPref = _.cloneDeep(userPrefStore)
      const lastVisitedUrlState = _.get(
        userPref,
        'config.lastVisitedUrl',
      )

      if (lastVisitedUrlState !== lastVisitedUrl) {
        _.set(userPref, 'config.lastVisitedUrl', lastVisitedUrl)
        debounceSave(userPref)
      }
    }

    openTenantSwitcher(false)
  }

  handleLogin = async () => { // can run mulitple times after odic relogin and app remount
    if (this.lite) {
      const { user_profile = {}, instance } = this.vars
      const supportAccountIds = (_.isString(user_profile.supportaccountids) && tryParseJson(user_profile.supportaccountids)) || []

      const main = {
        ...this.props.main,
        email: user_profile.email,
        userName: user_profile.userName || user_profile.email,
        isLoggedIn: true,
        infoLoaded: true,
        loggedInUser: {
          supportAccountIds,
          profile: user_profile,
        }
      }

      if (instance?.support_account_id > 0) {
        main.loggedInUser.currentSupportAccountId = instance.support_account_id
        main.loggedInUser.currentInstanceId = instance.tenant_id
      }
      setValuesInternal({ main })

      clearLoadingMask()
      authenticated.resolve({ lite: true })
      loaded.resolve(main)

      return syncLocationOnce()
    }
    if (!this.vars.oidc_client) {
      return this.handleFawkesLogin()
    }

    const { loginFailed } = this.props.state
    if (loginFailed || this.props.state.main?.isLoggedIn) {
      return // handle by render
    }
    if (this.props.oidc?.login && !this.props.oidc.oidcUser) {
      __info('clear session storage and login')
      sessionStorage.clear() // clear session storage before new login
      return this.props.oidc.login()
    }
    if (!this.props.oidc?.oidcUser) {
      console.error('oidc failed to init')
      const doc = window.document
      if (doc._page_load_from_cache) {
        if (Date.now() - doc._page_load_from_cache > 10000) { // 10s
          doc._page_load_from_cache = undefined
        }
        else {
          const referrerOrigin = doc.referrer && new URL(doc.referrer)?.origin
          if (!referrerOrigin || referrerOrigin === window.location.origin) {
            window.location.reload()
            return
          }
        }
      }
      if (this.vars.hub) {
        if (!sessionStorage._oidc_failed_reloading) {
          sessionStorage._oidc_failed_reloading = 1
          window.location.reload()
        }
        else {
          delete sessionStorage._oidc_failed_reloading
        }
      }
      return
    }
    // console.info(this.props.oidc)
    await handleOidcLogin(this.props.oidc)
    if (this.vars.hub) {
      delete sessionStorage._oidc_failed_reloading
    }
  }

  handleFawkesLogin = () => {
    const { loginFailed } = this.props.state
    // TODO: make it happen earlier to optimize
    const { isLoggedIn, noSession, jti } = getUserLoggedInInfo()
    if (!isLoggedIn) {
      if (!noSession && !loginFailed) {
        doLogin()
      }
      else {
        clearLoadingMask()
      }
      setMainValues({ noSession })
      return
    }
    if (jti) {
      initCache(jti)
    }
    return loadFawkesMainInfo().then(syncLocationOnce)
  }

  async componentDidMount() {
    setTimeout(this.handleLogin)
    if (this.props.state.loginFailed) {
      clearLoadingMask()
    }
    const onhashchange = () => {
      // @see sessionHelper
      if (window.location.hash === '#trylogin') {
        console.log('Session expired event trigger by session helper!')
        if (hasTsgSupport() && !isInSilentLoginIframe) {
          unsetSession()
          window.removeEventListener('hashchange', onhashchange)
          window.location.hash = ''
        }
        this.relogin()
      }
    }
    window.removeEventListener('hashchange', onhashchange)
    window.addEventListener('hashchange', onhashchange)
  }

  collapseBtnClicked = (args) => {
    if (this.inHubMode()) {
      return
    }
    const { isSideNavCollapsed } = this.props.state
    setValuesInternal({
      isSideNavCollapsed: !isSideNavCollapsed,
      _showSideMenu: false
    })
  }

  pendoInit = _.once(({ email, userName, supportAccountId: id, tsg_id }) => {
    this.pendo?.initialize({
      visitor: {
        id: email,
        email,
        displayName: userName
      },
      account: {
        id,
        tsg_id,
      }
    })
  })

  getUserTenantSupportAccountId() {
    const tenantCspId = this.props.main.tenant?.supportAccountId
    if (tenantCspId > 0) {
      return String(tenantCspId)
    }
    if (this.pendoAccountId > 0) {
      return this.pendoAccountId
    }
    const { supportAccountIds, currentSupportAccountId } = this.props.main.loggedInUser || this.props.auth?.user || {}
    if (currentSupportAccountId > 0) {
      this.pendoAccountId = String(currentSupportAccountId)
      return this.pendoAccountId
    }
    const instCspId = this.props.state.auth?.instances?.find(instance => instance.support_account_id > 0)?.support_account_id
    if (instCspId > 0) {
      this.pendoAccountId = String(instCspId)
      return this.pendoAccountId
    }
    const lastCspId = _.last(supportAccountIds)?.accountid
    if (lastCspId > 0) {
      this.pendoAccountId = String(lastCspId)
      return this.pendoAccountId
    }
    return ''
  }

  renderCortexAppBar(loggedinUser) {
    const { currentSupportAccountId, profile, email = profile?.email, supportAccountIds } = loggedinUser || {}
    const { firstname, lastname, supportaccountids = '[]' } = profile || {}
    const account = supportAccountIds?.find(account => account.accountid > 0 && +account.accountid === +currentSupportAccountId)
    const companyName = account?.name || 'Palo Alto Networks'
    const userName = `${firstname} ${lastname}`.trim() || email || ''
    const tsg_id = this.props.state.auth?.tsg_id || ''
    const userData = {
      userName,
      email,
      companyName,
    }
    const rbac = { user: 'data' }
    const { oidc } = this.props
    const hasPendo = Boolean(this.pendo)
    if (hasPendo) {
      // use customer's support account id if fawkes instance not available
      const supportAccountId = this.getUserTenantSupportAccountId()
      this.pendoInit({ supportAccountId, email, userName, tsg_id }) // will run only once
    }

    const leftMainNavigation = this.getOverridenNavigation()
    const { isSideNavCollapsed, sideMenuNavItem, leftMainActions, classicAppSwitcher } = this.props.state

    const isPrivateSite = Boolean(this.vars.private_site_origin)
    const hubMode = this.inHubMode()
    const showNonMspActions = this.vars.msp_tenant_switcher || this.vars.sase || this.vars.hub || isPrivateSite ? undefined : true
    const showUserMenu = this.lite ? undefined : showNonMspActions
    const showFeedbackButton = (hasPendo && (this.lite ? Boolean(this.vars.feedback) : showNonMspActions)) || undefined
    const [bottomActions, actions] = _.partition(leftMainActions, 'bottomMost')
    const hasFawkes = showNonMspActions || this.props.state.auth?.instances?.isFawkesRunning
    if (!showNonMspActions && !hubMode) {
      actions.push(...mspActions({ hasPendo, isPrivateSite, isSideNavCollapsed, oidc, vars: this.vars }))
    }
    else if (hasPendo && !isPrivateSite && !this.lite && !hubMode) {
      actions.push({
        key: 'announcements',
        title: _T('Announcements'),
        navComponent: Announcements,
      })
    }
    if (bottomActions.length) { // only allow one
      let [bottomAction] = bottomActions
      if (hubMode) {
        bottomAction = {
          ...bottomAction,
          mspActions: mspActions({ hasPendo, isPrivateSite, isSideNavCollapsed, oidc, vars: this.vars })
        }
      }
      actions.push(bottomAction)
    }

    const entitlements = classicAppSwitcher.visible && classicAppSwitcher.entitlements
    const allowLinkCallBackApps = classicAppSwitcher.allowLinkCallBackApps || (this.lite ? [] : ['*'])
    return (
      <cortex-app-bar
        id='pan-nav'
        rbac={JSON.stringify(rbac)}
        showAppSwitcher={classicAppSwitcher.visible || undefined}
        supportedAccounts={showNonMspActions ? supportaccountids : '[]'}
        userData={JSON.stringify(userData)}
        appTitle=''
        appBarLogo={leftMainNavigation.logo || this.vars.logo || logo}
        collapsedLogo={leftMainNavigation.collapsedLogo || this.vars.collapsed_logo || collapsedLogo}
        selectedAccount={currentSupportAccountId}
        _isVertical={true}
        _entitlements={entitlements ? JSON.stringify(entitlements) : '{}'}
        _noAppsMessage={entitlements ? _T('No apps found') : ''}
        showUserMenu={showUserMenu}
        letMeCollapse={'no'}
        isCollapsed={isSideNavCollapsed && !hubMode ? isSideNavCollapsed : null}
        collapsedWidth={50}
        allowLinkCallBackApps={JSON.stringify(allowLinkCallBackApps)}
        showFeedbackButton={showFeedbackButton}
      >
        <AppBar
          isSideNavCollapsed={isSideNavCollapsed && !hubMode}
          hideNavRoutes={hubMode}
          showSideMenu={this.showSideMenu}
          hideSideMenu={this.hideSideMenu}
          sideMenuNavItem={sideMenuNavItem}
          onTreeNodeSelected={this.onTreeNodeSelected}
          actions={actions}
          hasFawkes={hasFawkes}
          configLocation={this.props.main.configLocation}
          licenseInfo={this.props.main.licenseInfo}
          featuresInfo={this.props.main.featuresInfo}
          navigation={leftMainNavigation}
          state={this.props.state}
        />
      </cortex-app-bar>
    )
  }
  getOverridenNavigation = () => {
    const { location: { pathname } } = this.props
    let overriddenNav = this.props.state.leftMainNavigation.reduce((acc, item) => {
      if (item.overrideLeftNav?.match) {
        const matchValue = item[item.overrideLeftNav.match]
        if (matchValue && item.overrideLeftNav.match === 'path' && pathname?.startsWith(matchValue)) {
          const overrideItems = item.overrideLeftNav.with ? item[item.overrideLeftNav.with] : item
          if (Array.isArray(overrideItems)) {
            acc.push(...overrideItems.map(nav => ({ className: 'parent-node', ...nav })))
          }
          else {
            acc.push(overrideItems)
          }
          if (item.overrideLeftNav.logo) {
            acc.logo = item.overrideLeftNav.logo
          }
          if (item.overrideLeftNav.collapsedLogo) {
            acc.collapsedLogo = item.overrideLeftNav.collapsedLogo
          }
        }
      }
      return acc
    }, [])
    if (!overriddenNav.length) {
      overriddenNav = this.props.state.leftMainNavigation
      if (getFrameworkVar('show_approved_nav_only') && hasMicroApp('sparky-one-nav') && overriddenNav?.length) {
        const [approved, nonapproved] = _.partition(overriddenNav, (item) =>
          item.__approved_item || item.hidden || item.overrideLeftNav
        )
        overriddenNav = approved
        if (nonapproved.length) {
          console.error('non-approved nav items!', nonapproved)
        }
      }
    }
    const leftMainNavigation = overriddenNav.map((nav) => {
      if (nav.path !== '#' && isNonemptyArray(nav.children) && !nav.component?.layout) {
        return {
          ...nav,
          alternateKey: nav.alternateKey || nav.path,
          path: '#',
        }
      }
      return nav
    })
    if (overriddenNav.logo) {
      leftMainNavigation.logo = overriddenNav.logo
    }
    if (overriddenNav.collapsedLogo) {
      leftMainNavigation.collapsedLogo = overriddenNav.collapsedLogo
    }
    return leftMainNavigation
  }

  onTreeNodeSelected = (treeNode, toggleHide) => {
    if (!treeNode) {
      return
    }
    const { isSideNavCollapsed, sideMenuNavItem } = this.props.state
    //revisit this logic
    if (treeNode.expandable) {
      if (toggleHide && isSideNavCollapsed && treeNode.key === sideMenuNavItem?.key) {
        this.hideSideMenu(treeNode)
      }
      else {
        this.showSideMenu(treeNode)
      }
    }
    if (!treeNode.expandable) {
      this.hideSideMenu()
    }
  }

  showSideMenu = (treeNode) => {
    if (this.inHubMode()) {
      return
    }
    setValuesInternal({
      sideMenuNavItem: treeNode,
      _showSideMenu: true,
      isSideNavCollapsed: true
    })
  }
  hideSideMenu = () => {
    if (this.inHubMode()) {
      return
    }
    setValuesInternal({
      isSideNavCollapsed: false,
      _showSideMenu: false,
    })
  }
  renderSideMenuDrawer() {
    const { _showSideMenu, sideMenuNavItem, leftMainNavigation } = this.props.state
    return (
      <LeftNavWrap
        _showSideMenu={_showSideMenu}
        menu={sideMenuNavItem}
        pathAvailMap={leftMainNavigation.pathAvailMap}
        flattenNavigationList={leftMainNavigation.flattenNavigationList}
        main={this.props.main}
      />
    )
  }

  updateRouteValue = (key, val) => {
    setValuesInternal({ [key]: val })
  }

  updateRouteState = () => {
    const { loggedInUser, state: { commonModule, ...state } } = this.props
    const lastVisitedUrl = _.get(loggedInUser, 'preference.config.lastVisitedUrl', '/')
    return { ...state, lastVisitedUrl }
  }

  getModalProps() {
    const { noSession } = this.props.main
    const { loginFailed, ipRestrict } = this.props.state
    if (loginFailed) {
      let message
      const reload = () => window.location.reload()
      if (loginFailed.message) {
        message = loginFailed.message
      }
      else if (ipRestrict) {
        message = _T('You are accessing from an unauthorized address. Please check with your administrator.')
      }
      else if (window.location.search.includes('invalidSession=true')) {
        message = _T('Your session may have expired or you do not have access to this tenant.')
      }
      else if (noSession) {
        message = _T('We did not find a tenant id to log you in. Please navigate to this application from Hub to continue.')
      }
      else {
        message = _T('We are unable to log you in. Your session may have expired or you do not have access to this tenant.')
      }
      return {
        id: 'session-error',
        key: 'session-error',
        title: _T('Error'),
        type: 'Error',
        size: 'lg',
        ...loginFailed,
        onClose: loginFailed.reload ? reload : logout,
        message,
        closeText: loginFailed.reload ? _T('Reload') : _T('Close'),
      }
    }
    else if (noSession) { // TODO: show fullscreen error instead
      return {
        id: 'session-error',
        key: 'session-error',
        size: 'xl',
        title: _T('Error'),
        onClose: logout,
        message: _T('We did not find a tenant id to log you in. Please navigate to this application from Hub to continue.'),
        closeText: _T('Close'),
      }
    }
    return undefined
  }

  render() {
    const main = this.props.main
    const { tenant, isLoggedIn, loggedInUser = this.props.auth?.user, licenseInfo, featuresInfo, tokenInfo } = main
    const { navRoutes, _showSideMenu, leftMainNavigation, auth } = this.props.state
    const defaultRoute = StartupManager.defaultRedirect({ auth, vars: this.vars })
    const fatalModalProps = this.getModalProps()
    if (loggedInUser === null) {
      return <LoadingPlaceholder />
    }
    if (fatalModalProps) {
      const { id, key, size, onClose, title, message, closeText } = fatalModalProps
      return <Modal
        id={id}
        key={key}
        isOpen={true}
        onClose={onClose}
        size={size}
        style={{
          minHeight: 50,
        }}
      >
        <ModalHeader title={title} />
        <ModalBody>{message}</ModalBody>
        <ModalFooter>
          <ModalCloseButton>{closeText}</ModalCloseButton>
        </ModalFooter>
      </Modal>
    }
    return Boolean(main.infoLoaded) && !leftMainNavigation.initializing && !navRoutes.loading && isLoggedIn === true && !_.isEmpty(loggedInUser) ? (
      <RouteProvider value={{ ...this.updateRouteState(), updateRouteValue: this.updateRouteValue, changeConfigLocation: this.changeConfigLocation }}>
        <RouteViewer
          appBar={this.renderCortexAppBar(loggedInUser)}
          sideMenuDrawer={this.renderSideMenuDrawer()}
          loggedInUser={loggedInUser}
          tenant={tenant}
          navigation={navRoutes}
          configLocation={main.configLocation}
          licenseInfo={licenseInfo}
          featuresInfo={featuresInfo}
          hideSideMenu={this.hideSideMenu}
          _showSideMenu={_showSideMenu}
          state={this.props.state}
          getState={this.props.getState}
          stateRef={this.props.stateRef}
          dispatch={this.props.dispatch}
          events={this.props.events}
          routeContext={{ ...this.updateRouteState(), updateRouteValue: this.updateRouteValue, changeConfigLocation: this.changeConfigLocation }}
          salt={getMd5Salt()}
          tokenInfo={tokenInfo}
          defaultRoute={defaultRoute}
          RouteWrapper={this.vars.memo_route_component !== false ? MemoComponentWrapper : ComponentWrapper}
        />
      </RouteProvider>
    ) : <LoadingPlaceholder /> // splash screen should be shown most of the loading time
  }
}

export default withRouter(Main)
